import React from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * Counter: minus / value / plus stepper (e.g. Present, Spoiled, Removed).
 * Props:
 * - value (number), onChange(nextValue)
 * - min (default 0), max (optional), step (default 1)
 * - label: caption above, also used for the button labels ("Less present")
 * - formatValue(value): optional display text for the value (e.g. scaled units); steps stay raw
 * - compact: smaller buttons and value (touch area kept at 44 by hitSlop) so three counters with
 *   3-digit values fit one row on narrow phones
 * - disabled, style
 */
const Counter = ({
	value = 0,
	onChange,
	min = 0,
	max,
	step = 1,
	label,
	formatValue,
	disabled = false,
	compact = false,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const canDecrease = !disabled && value - step >= min;
	const canIncrease = !disabled && (max === undefined || max === null || value + step <= max);
	const labelText = label ? label.toLowerCase() : 'value';
	const displayValue = formatValue ? formatValue(value) : value;

	const renderButton = (icon, isEnabled, nextValue, accessibilityLabel) => (
		<Pressable
			onPress={isEnabled ? () => onChange?.(nextValue) : undefined}
			disabled={!isEnabled}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityState={{ disabled: !isEnabled }}
			hitSlop={compact ? COMPACT_HIT_SLOP : undefined}
			style={({ pressed }) => [styles.button, compact && styles.buttonCompact, !isEnabled && styles.buttonDisabled, pressed && styles.pressed]}
		>
			<Feather name={icon} size={compact ? 16 : 18} color={isEnabled ? colors.accent : colors.disabledText} />
		</Pressable>
	);

	return (
		<View style={[styles.container, style]}>
			{Boolean(label) && <Text style={styles.label}>{label}</Text>}
			<View style={styles.controls} accessible={false}>
				{renderButton('minus', canDecrease, value - step, `Less ${labelText}`)}
				<Text style={[styles.value, compact && styles.valueCompact]} accessibilityLiveRegion="polite" accessibilityLabel={`${label || 'Value'} ${displayValue}`}>
					{displayValue}
				</Text>
				{renderButton('plus', canIncrease, value + step, `More ${labelText}`)}
			</View>
		</View>
	);
};

const COMPACT_BUTTON_SIZE = 36;
// Brings the compact button's touch area back to 44 pt.
const COMPACT_HIT_SLOP = 4;

const createStyles = ({ ui: { colors, radius, touch, type } }) => ({
	container: {
		alignItems: 'center',
		gap: 4,
	},
	label: {
		...type.tag,
		color: colors.textSecondary,
	},
	controls: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	button: {
		width: touch.min,
		height: touch.min,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonCompact: {
		width: COMPACT_BUTTON_SIZE,
		height: COMPACT_BUTTON_SIZE,
	},
	buttonDisabled: {
		borderColor: colors.disabledBg,
		backgroundColor: colors.disabledBg,
	},
	pressed: {
		opacity: 0.8,
	},
	value: {
		...type.cardTitle,
		minWidth: 28,
		textAlign: 'center',
		color: colors.text,
	},
	valueCompact: {
		...type.sectionTitle,
		minWidth: 24,
		marginHorizontal: 2,
	},
});

export default Counter;
